import { useEffect, useState } from 'react';
import {
  VITE_SALES_ASSISTANT_ENABLED,
  VITE_WORKSPACE_AGENT_ENABLED,
} from 'src/constants/envs';
import { useWorkspaceAgent } from 'src/providers/WorkspaceAgentProvider/Workspace.context';
import { useWorkspaceAgentRollout } from 'src/providers/WorkspaceAgentProvider/Workspace.rollout.hook';
import { useWorkspaceContext } from 'src/providers/WorkspaceProvider/Workspace.context';
import { useUserContext } from 'src/providers/UserProvider/User.context';
import { eUserRole } from 'src/providers/UserProvider/User.enums';
import { useSubscriptionContext } from 'src/providers/SubscriptionProvider/Subscription.context';
import { ePlanCode } from 'src/providers/SubscriptionProvider/Subscription.enums';
import { assistantAccess } from './data/Assistant.access';
import {
  AssistantFailure,
  type tAssistantAccess,
} from './data/Assistant.types';

export function useAssistantAccess() {
  const rolloutEnabled = useWorkspaceAgentRollout();
  const nativeAgent = useWorkspaceAgent();
  const { currentWorkspaceId } = useWorkspaceContext();
  const { currentUserId, currentUserRole } = useUserContext();
  const { subscription, hasSubscriptionData } = useSubscriptionContext();
  const planCode = hasSubscriptionData ? subscription.planCode : undefined;
  const scope = `${currentWorkspaceId}:${currentUserId}:${currentUserRole}:${planCode}`;
  const [state, setState] = useState<{
    scope: string;
    access?: tAssistantAccess;
    failed?: boolean;
    planDenied?: boolean;
  }>();
  const [revision, setRevision] = useState(0);
  const enabled =
    VITE_SALES_ASSISTANT_ENABLED &&
    rolloutEnabled &&
    !!currentWorkspaceId &&
    !!currentUserId;
  useEffect(() => {
    let cancelled = false;
    if (enabled && !VITE_WORKSPACE_AGENT_ENABLED && currentWorkspaceId) {
      setState(undefined);
      void assistantAccess({ workspaceId: currentWorkspaceId }).then(
        (access) => {
          if (!cancelled) setState({ scope, access });
        },
        (error: unknown) => {
          if (!cancelled)
            setState({
              scope,
              failed: true,
              planDenied:
                error instanceof AssistantFailure &&
                error.code === 'upgradeRequired',
            });
        },
      );
    }
    return () => {
      cancelled = true;
    };
  }, [enabled, scope, revision]);
  if (VITE_WORKSPACE_AGENT_ENABLED) {
    const ready =
      enabled && hasSubscriptionData && nativeAgent.status === 'ready';
    const access: tAssistantAccess | undefined =
      ready && nativeAgent.access.capabilities.crm.available
        ? {
            allowed: true,
            role: nativeAgent.access.role,
            campaignsEnabled: false,
          }
        : undefined;
    const upgradeRequired =
      ready &&
      nativeAgent.access.capabilities.crm.reason === 'plan_not_eligible' &&
      (planCode === ePlanCode.PLAN_BASIC || planCode === ePlanCode.PLAN_LEGACY);
    return {
      native: true,
      enabled,
      scope,
      workspaceId: currentWorkspaceId,
      access,
      upgradeRequired,
      visible: !!access || upgradeRequired,
      canUpgrade: currentUserRole === eUserRole.OWNER,
      failed:
        enabled &&
        (nativeAgent.status === 'unavailable' ||
          (ready && !access && !upgradeRequired)),
      retry: nativeAgent.retry,
    };
  }
  const current = enabled && state?.scope === scope ? state : undefined;
  const upgradeRequired =
    current?.planDenied === true &&
    (planCode === ePlanCode.PLAN_BASIC || planCode === ePlanCode.PLAN_LEGACY);
  return {
    native: false,
    enabled,
    scope,
    workspaceId: currentWorkspaceId,
    access: current?.access,
    upgradeRequired,
    visible: current?.access?.allowed === true || upgradeRequired,
    canUpgrade: currentUserRole === eUserRole.OWNER,
    failed: current?.failed === true && !upgradeRequired,
    retry: () => setRevision((value) => value + 1),
  };
}
